import Phaser from 'phaser';
import { GAME_CONFIG } from '../../config/game';
import { BaseRunScene, RunSceneConfig } from './BaseRunScene';

/**
 * MissingShoeScene (Level 1-3)
 * 
 * Theme: Finding the missing shoe in a messy mudroom
 * Goal: Find the matching shoe while dodging obstacles
 * 
 * Mechanics:
 * - Collect shoes to check them
 * - Wrong shoes cause damage
 * - Matching shoe wins the level
 * 
 * Pity system:
 * - After 2 checks: 40% chance of match
 * - After 4 checks: guaranteed match
 * - If match is missed, next spawn is also guaranteed
 */
export class MissingShoeScene extends BaseRunScene {
  private spawnTimer?: Phaser.Time.TimerEvent;
  private shoeTimer?: Phaser.Time.TimerEvent;
  private targetShoeColor: number = 0;
  private foundMatchingShoe: boolean = false;
  private shoesChecked: number = 0;
  private shoeColors: number[] = [0xff6b6b, 0x4ecdc4, 0xffd93d, 0x6bcb77, 0x4d96ff];
  private shoesCheckedText?: Phaser.GameObjects.Text;
  
  // Pity system: track if we need to force a match
  private forceNextMatch: boolean = false;
  private matchSpawned: boolean = false; // Track if a match is currently on screen
  private wrongShoeCount: number = 0; // Track wrong shoes for damage reduction
  private warmthHint?: Phaser.GameObjects.Text; // "Getting warmer" indicator

  constructor() {
    const config: RunSceneConfig = {
      levelId: '1-3',
      levelName: 'The Missing Shoe Quest',
      backgroundKey: '1-3_missing_shoe',
      targetDistance: 1200, // Reasonable length
      scrollSpeed: 90, // Comfortable speed
    };
    super('MissingShoeScene', config);
  }

  /**
   * Check if scene is still valid for operations
   */
  private isSceneValid(): boolean {
    return this.scene && this.scene.isActive(this.scene.key) && !this.isGameOver;
  }

  /**
   * Safe delayed call that checks scene validity
   */
  private safeDelayedCall(delay: number, callback: () => void): Phaser.Time.TimerEvent {
    return this.time.delayedCall(delay, () => {
      if (this.isSceneValid()) {
        callback();
      }
    });
  }

  create() {
    super.create();
    
    // Reset state
    this.targetShoeColor = this.shoeColors[Phaser.Math.Between(0, this.shoeColors.length - 1)];
    this.foundMatchingShoe = false;
    this.shoesChecked = 0;
    this.forceNextMatch = false;
    this.matchSpawned = false;
    this.wrongShoeCount = 0;
    this.warmthHint = undefined;
    
    // Show the target shoe UI
    this.createTargetShoeUI();
  }

  private createTargetShoeUI() {
    if (!this.isSceneValid()) return;
    
    const { width } = GAME_CONFIG;
    
    // Background panel
    const panel = this.add.graphics();
    panel.fillStyle(0x000000, 0.6);
    panel.fillRoundedRect(width / 2 - 90, 78, 180, 55, 8);
    panel.setDepth(100);

    // Label
    this.add.text(width / 2, 90, 'Find this shoe:', {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(100);

    // Draw target shoe (larger)
    const shoeIcon = this.add.graphics();
    shoeIcon.fillStyle(this.targetShoeColor, 1);
    shoeIcon.fillRoundedRect(-20, -10, 40, 20, 6);
    shoeIcon.fillStyle(0xffffff, 0.5);
    shoeIcon.fillCircle(-8, -3, 4);
    shoeIcon.setPosition(width / 2, 115);
    shoeIcon.setDepth(100);
    
    // Counter
    this.shoesCheckedText = this.add.text(width / 2, 128, 'Checked: 0', {
      fontFamily: 'Georgia, serif',
      fontSize: '10px',
      color: '#aaaaaa',
    }).setOrigin(0.5).setDepth(100);
  }

  protected startSpawning() {
    if (!this.isSceneValid()) return;
    
    // Spawn obstacles at moderate rate
    this.spawnTimer = this.time.addEvent({
      delay: 1400,
      callback: () => {
        if (this.isSceneValid()) this.spawnObstacle();
      },
      callbackScope: this,
      loop: true,
    });

    // Spawn shoes regularly
    this.shoeTimer = this.time.addEvent({
      delay: 1200,
      callback: () => {
        if (this.isSceneValid()) this.spawnCollectible();
      },
      callbackScope: this,
      loop: true,
    });
    
    // Spawn first shoe quickly
    this.safeDelayedCall(500, () => this.spawnCollectible());
  }

  protected spawnObstacle() {
    if (!this.isSceneValid() || this.isPaused) return;

    const { width, height } = GAME_CONFIG;
    const y = height - 100;

    const obstacle = this.add.graphics();
    const itemType = Phaser.Math.Between(0, 2);
    
    if (itemType === 0) {
      // Box
      obstacle.fillStyle(0xd2691e, 1);
      obstacle.fillRect(-20, -15, 40, 30);
    } else if (itemType === 1) {
      // Backpack
      obstacle.fillStyle(0x8b4513, 1);
      obstacle.fillRoundedRect(-15, -20, 30, 40, 5);
    } else {
      // Umbrella
      obstacle.fillStyle(0x333333, 1);
      obstacle.fillRect(-5, -30, 10, 50);
    }

    const container = this.add.container(width + 30, y, [obstacle]);
    this.physics.add.existing(container);
    
    const body = container.body as Phaser.Physics.Arcade.Body;
    body.setSize(35, 40);
    body.setAllowGravity(false);
    
    this.obstacles.add(container);
  }

  protected spawnCollectible() {
    if (!this.isSceneValid() || this.isPaused || this.foundMatchingShoe) return;

    const { width, height } = GAME_CONFIG;
    // Spawn at jumpable heights
    const y = Phaser.Math.Between(height - 250, height - 120);

    // Determine if this shoe should be a match
    let isMatch = false;
    
    // Force match if pity system requires it
    if (this.forceNextMatch) {
      isMatch = true;
      this.forceNextMatch = false;
    }
    // Guarantee match after 3 checks (was 4) - more forgiving
    else if (this.shoesChecked >= 3) {
      isMatch = true;
    }
    // 60% chance after 2 checks (was 50% after 3)
    else if (this.shoesChecked >= 2) {
      isMatch = Math.random() < 0.6;
    }
    // 40% chance after 1 check (was 35% after 2)
    else if (this.shoesChecked >= 1) {
      isMatch = Math.random() < 0.4;
    }
    // 20% chance on first spawn (was 15%)
    else {
      isMatch = Math.random() < 0.2;
    }
    
    // Track if match is on screen
    if (isMatch) {
      this.matchSpawned = true;
    }
    
    const shoeColor = isMatch 
      ? this.targetShoeColor 
      : this.shoeColors.filter(c => c !== this.targetShoeColor)[Phaser.Math.Between(0, 3)];

    // Create shoe
    const glow = this.add.graphics();
    glow.fillStyle(isMatch ? 0xffd700 : 0xffffff, isMatch ? 0.5 : 0.2);
    glow.fillCircle(0, 0, isMatch ? 35 : 25);

    const shoe = this.add.graphics();
    shoe.fillStyle(shoeColor, 1);
    shoe.fillRoundedRect(-18, -10, 36, 20, 6);
    shoe.fillStyle(0xffffff, 0.5);
    shoe.fillCircle(-8, -3, 4);

    const container = this.add.container(width + 30, y, [glow, shoe]);
    
    // Store match status ON the container
    container.setData('isMatch', isMatch);
    
    // Add sparkle for matching shoe
    if (isMatch) {
      const sparkle = this.add.text(0, -28, '⭐', { fontSize: '20px' });
      sparkle.setOrigin(0.5);
      container.add(sparkle);
      
      this.tweens.add({
        targets: sparkle,
        alpha: { from: 0.5, to: 1 },
        scale: { from: 0.8, to: 1.3 },
        duration: 300,
        yoyo: true,
        repeat: -1,
      });

      // Strong glow pulse for match
      this.tweens.add({
        targets: glow,
        scaleX: 1.5,
        scaleY: 1.5,
        alpha: 0.3,
        duration: 400,
        yoyo: true,
        repeat: -1,
      });
    }
    
    this.physics.add.existing(container);
    const body = container.body as Phaser.Physics.Arcade.Body;
    body.setSize(50, 35);
    body.setAllowGravity(false);
    
    this.collectibles.add(container);

    // Float animation
    this.tweens.add({
      targets: container,
      y: y - 10,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  // Override the base collectible handler to check match BEFORE destroying
  protected handleCollectiblePickup(
    _player: Phaser.GameObjects.GameObject,
    collectible: Phaser.GameObjects.GameObject
  ) {
    if (!this.isSceneValid() || this.isPaused) return;
    
    // Check if this is the matching shoe BEFORE destroying
    const isMatch = collectible.getData('isMatch') === true;
    
    // If this was a match shoe, track that we picked it up
    if (isMatch) {
      this.matchSpawned = false;
    }
    
    // Destroy the collectible
    collectible.destroy();
    
    // Guard again after destruction
    if (!this.isSceneValid()) return;
    
    this.collected++;
    this.shoesChecked++;
    
    // Update counter safely
    if (this.shoesCheckedText && this.shoesCheckedText.scene) {
      this.shoesCheckedText.setText(`Checked: ${this.shoesChecked}`);
    }
    
    // Handle match or wrong shoe
    if (isMatch) {
      this.foundMatchingShoe = true;
      this.showMatchFound();
    } else {
      this.wrongShoeCount++;
      
      // First wrong shoe is free! (no damage)
      if (this.wrongShoeCount === 1) {
        this.showWrongShoe(true); // true = no damage
      } else {
        // Subsequent wrong shoes deal damage
        const alive = this.player.takeDamage();
        this.updateHearts();
        this.showWrongShoe(false); // false = took damage
        
        if (!alive) {
          this.onDefeat();
          return;
        }
        
        // Mild speed increase after wrong shoe
        this.scrollSpeed += 8;
      }
      
      // If we were supposed to get a guaranteed match but didn't (it scrolled off screen),
      // force the next one to be a match
      if (this.shoesChecked >= 3 && !this.matchSpawned) {
        this.forceNextMatch = true;
      }
    }
  }

  private showMatchFound() {
    if (!this.isSceneValid()) return;
    
    const { width, height } = GAME_CONFIG;
    
    // Stop spawning more shoes
    this.shoeTimer?.destroy();
    this.spawnTimer?.destroy();
    
    const text = this.add.text(width / 2, height / 2, '👟 FOUND IT! 👟', {
      fontFamily: 'Georgia, serif',
      fontSize: '32px',
      color: '#ffd700',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(200);

    this.cameras.main.shake(200, 0.01);

    this.tweens.add({
      targets: text,
      scale: { from: 0.5, to: 1.2 },
      duration: 400,
      onComplete: () => {
        if (!this.isSceneValid()) {
          if (text && text.scene) text.destroy();
          return;
        }
        this.safeDelayedCall(800, () => {
          if (text && text.scene) text.destroy();
          // Trigger victory
          if (this.isSceneValid()) {
            this.config.targetDistance = this.distance;
          }
        });
      },
    });
  }

  private showWrongShoe(noDamage: boolean = false) {
    if (!this.isSceneValid()) return;
    
    const { width } = GAME_CONFIG;
    
    let message: string;
    let color: string;
    
    if (noDamage) {
      // First wrong shoe - gentle feedback
      const freeMessages = ['Wrong one!', 'Nope!', 'Not it!', 'Keep looking!'];
      message = freeMessages[Phaser.Math.Between(0, 3)];
      color = '#ffaa44'; // Orange - warning but not damage
    } else {
      // Subsequent wrong shoes - damage feedback
      const damageMessages = ['OUCH! Wrong!', 'Not it! -♥', 'Wrong shoe!', 'That hurt!'];
      message = damageMessages[Phaser.Math.Between(0, 3)];
      color = '#ff4444';
      // Screen flash red only when taking damage
      this.cameras.main.flash(200, 255, 0, 0);
    }
    
    const text = this.add.text(width / 2, 160, message, {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: color,
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(150);

    this.tweens.add({
      targets: text,
      y: 130,
      alpha: 0,
      scale: 1.3,
      duration: 800,
      onComplete: () => {
        if (text && text.scene) text.destroy();
      },
    });
  }

  // Required by BaseRunScene but not used since we override handleCollectiblePickup
  protected onCollect() {
    // Handled in handleCollectiblePickup override
  }

  protected updateLevel(_time: number, _delta: number) {
    // Cap speed
    this.scrollSpeed = Math.min(180, this.scrollSpeed);
    
    // Show "getting warmer" hint when match shoe is visible on screen
    this.updateWarmthHint();
    
    // Track if match shoe scrolled off screen (missed)
    // This is handled implicitly - collectibles are destroyed when off-screen
    // and if it was a match, matchSpawned stays true, triggering forceNextMatch
  }
  
  private updateWarmthHint() {
    if (!this.isSceneValid()) return;
    
    const { width } = GAME_CONFIG;
    
    // Check if any collectible on screen is a match
    let matchVisible = false;
    this.collectibles.getChildren().forEach((child) => {
      const container = child as Phaser.GameObjects.Container;
      if (container.getData('isMatch') && container.x < width - 50) {
        matchVisible = true;
      }
    });
    
    // Show or hide hint
    if (matchVisible && !this.warmthHint) {
      this.warmthHint = this.add.text(width / 2, 150, '🔥 Getting warmer! 🔥', {
        fontFamily: 'Georgia, serif',
        fontSize: '14px',
        color: '#ffdd44',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5).setDepth(150);
      
      // Pulse animation
      this.tweens.add({
        targets: this.warmthHint,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 400,
        yoyo: true,
        repeat: -1,
      });
    } else if (!matchVisible && this.warmthHint) {
      this.warmthHint.destroy();
      this.warmthHint = undefined;
    }
  }

  // Clean up on shutdown
  shutdown() {
    super.shutdown();
    this.spawnTimer?.destroy();
    this.shoeTimer?.destroy();
    this.spawnTimer = undefined;
    this.shoeTimer = undefined;
    
    // Clear warmth hint
    if (this.warmthHint) {
      this.warmthHint.destroy();
      this.warmthHint = undefined;
    }
  }
}
